import React, {useState} from 'react';
import TablePagination from '../../components/TablePagination/TablePagination';

export type Numeric = number | string;
export const formatter = new Intl.NumberFormat('bs-BA', {minimumFractionDigits: 2, maximumFractionDigits: 2});
export const kg = (value: Numeric | null | undefined) =>
    value == null || value === '' || !Number.isFinite(Number(value)) ? '—' : formatter.format(Number(value)) + ' kg';
export const tonnes = (value: Numeric | null | undefined) =>
    value == null || value === '' || !Number.isFinite(Number(value)) ? '—' : formatter.format(Number(value) / 1000) + ' t';
export type Column<T> = {title: string; render: (row: T) => React.ReactNode; totalValue?: (row: T) => number};
export function ReportTable<T extends {RolnaId?: Numeric; TrakaId?: Numeric}>({rows, columns, label, showTotals = false, entity = 'rolne'}: {
    rows: T[]; columns: Column<T>[]; label: string; showTotals?: boolean; entity?: 'rolne' | 'trake';
}) {
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const filtered = rows.filter(row => String((row.RolnaId ?? row.TrakaId)).includes(search.trim()));
    const totalPage = Math.max(1, Math.ceil(filtered.length / perPage));
    const curPage = Math.min(page, totalPage);
    return <>
        <input className='form-control mb-4' aria-label={'Pretraga po ID-u ' + entity + ': ' + label}
            placeholder={'Pretraga po ID-u ' + entity} value={search}
            onChange={event => {setSearch(event.target.value); setPage(1);}} />
        <div className='table-responsive'>
            <table className='table table-striped table-bordered align-middle' aria-label={label}>
                <thead><tr><th scope='col'>Redni broj</th>{columns.map(column => <th key={column.title} scope='col'>{column.title}</th>)}</tr></thead>
                <tbody>{filtered.slice((curPage - 1) * perPage, curPage * perPage).map((row, index) =>
                    <tr key={String((row.RolnaId ?? row.TrakaId)) + '-' + index}><td>{(curPage - 1) * perPage + index + 1}</td>{columns.map(column =>
                        <td key={column.title}>{column.render(row)}</td>)}</tr>)}
                {!filtered.length && <tr><td colSpan={columns.length + 1}>
                    {search ? 'Nema rezultata pretrage.' : 'Nema stavki za prikaz.'}
                </td></tr>}</tbody>
                {showTotals && <tfoot className='fw-bold border-top border-2'>
                    <tr>{columns.map((column, index) => index === 0 ?
                        <th key={column.title} scope='row' colSpan={2}>Ukupno{search.trim() ? ' (filtrirano)' : ''} — {filtered.length} {entity === 'trake' ? 'traka' : 'rolni'}</th> :
                        <td key={column.title}>{column.totalValue
                            ? kg(filtered.reduce((sum, row) => sum + column.totalValue!(row), 0))
                            : '—'}</td>)}</tr>
                </tfoot>}
            </table>
        </div>
        <p className='text-muted'>Broj stavki: {filtered.length}</p>
        <TablePagination pagination={{curPage, totalPage, perPage}} updatePage={(next: number, size: number) => {
            setPerPage(size);
            setPage(size !== perPage ? 1 : Math.max(1, Math.min(next, totalPage)));
        }} />
    </>;
}
export const Metric = ({title, value, description, tonnesValue, valueClassName}: {
    title: string; value: React.ReactNode; description?: string; tonnesValue?: string; valueClassName?: string;
}) =>
    <div className='col-12 col-sm-6 col-xl-4 mb-4'><div className='card h-100'><div className='card-body'>
        <div className={tonnesValue ? 'fw-bold text-gray-800 mb-3' : 'text-muted mb-2'}>{title}</div>
        <div className={valueClassName ?? (tonnesValue ? 'fs-1 fw-bolder text-primary' : 'fs-2 fw-bold')}>{value}</div>
        {tonnesValue && <div className='mt-2 d-inline-flex flex-wrap align-items-center gap-2 bg-light-primary rounded px-3 py-2'>
            <span className='text-gray-700'>U tonama:</span><strong className='fs-5 text-primary'>{tonnesValue}</strong>
        </div>}
        {description && <p className='text-muted mt-3 mb-0'>{description}</p>}
    </div></div></div>;

